import type { CSSProperties, ReactNode } from 'react';

import { usePublicPageScroll } from '@/components/public/usePublicPageScroll';

interface PublicLayoutProps {
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  /** Overrides the page colour, e.g. a public form's chosen background. */
  style?: CSSProperties;
}

const maxWidthClass = {
  sm: 'max-w-lg',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-7xl',
};

/**
 * Full-width shell for standalone public documents (invoice, payment link).
 *
 * Shares one scroll-cage release with `PublicCenteredPage`. This used to clear
 * `overflow` alone, which left `#root` at `height:100%` and so made #root — not
 * the document — the scroller: the wheel worked, but there was no document
 * scrollbar and the keyboard could not move the page.
 */
export function PublicLayout({ children, maxWidth = 'md', style }: PublicLayoutProps) {
  usePublicPageScroll();

  return (
    <div className="min-h-dvh bg-gray-50" style={style}>
      <main className={`mx-auto ${maxWidthClass[maxWidth]} px-4 py-8 sm:px-6`}>{children}</main>
    </div>
  );
}
